import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { homeButtonsApi } from '../../../services/apiClient';

/** Default buttons matching the resident app's built-in set */
const DEFAULT_BUTTONS = [
  { id: 'contacts',     icon: '📞', label: 'Contacts',     color: '#4299E1', actionType: 'navigation', actionTarget: '/contacts',     enabled: true },
  { id: 'photos',       icon: '📷', label: 'Photos',       color: '#48BB78', actionType: 'navigation', actionTarget: '/photos',       enabled: true },
  { id: 'info',         icon: '📋', label: 'Info',         color: '#9F7AEA', actionType: 'navigation', actionTarget: '/info',         enabled: true },
  { id: 'games',        icon: '🎮', label: 'Games',        color: '#E53E3E', actionType: 'navigation', actionTarget: '/games',        enabled: true },
  { id: 'voicemail',    icon: '📬', label: 'Voicemail',    color: '#DD6B20', actionType: 'navigation', actionTarget: '/voicemail',    enabled: true },
  { id: 'call-history', icon: '📞', label: 'Call History',  color: '#3182CE', actionType: 'navigation', actionTarget: '/call-history', enabled: true },
  { id: 'settings',     icon: '⚙️', label: 'Settings',     color: '#718096', actionType: 'navigation', actionTarget: '/settings',     enabled: true },
];

const ACTION_TYPES = [
  { value: 'navigation',   label: 'Navigate (in-app)' },
  { value: 'videophone',   label: 'Videophone / Call' },
  { value: 'alarm',        label: 'Alarm / Emergency' },
  { value: 'door_unlock',  label: 'Door unlock' },
  { value: 'qbus_control', label: 'Qbus control' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'custom',       label: 'Custom' },
];

const HomeScreenButtonsTab = ({ person }) => {
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newButton, setNewButton] = useState({
    icon: '📱',
    label: '',
    color: '#4299E1',
    actionType: 'navigation',
    actionTarget: '',
  });

  // Fetch existing button config for this person
  const fetchButtons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await homeButtonsApi.getByPerson(person.id);
      const data = response.data?.data?.buttons || response.data?.buttons || response.data;
      if (Array.isArray(data) && data.length > 0) {
        setButtons(data.map((b, i) => ({ ...b, order: b.order ?? i, enabled: b.enabled !== false })));
      } else {
        // No saved config — use defaults
        setButtons(DEFAULT_BUTTONS.map((b, i) => ({ ...b, order: i })));
      }
    } catch (err) {
      // 404 = no config yet, use defaults
      if (err.response?.status === 404) {
        setButtons(DEFAULT_BUTTONS.map((b, i) => ({ ...b, order: i })));
      } else {
        setError('Failed to load button configuration');
        setButtons(DEFAULT_BUTTONS.map((b, i) => ({ ...b, order: i })));
      }
    } finally {
      setLoading(false);
    }
  }, [person.id]);

  useEffect(() => {
    fetchButtons();
  }, [fetchButtons]);

  const handleToggle = (index) => {
    setButtons(prev => prev.map((b, i) =>
      i === index ? { ...b, enabled: !b.enabled } : b
    ));
    setHasChanges(true);
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    setButtons(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((b, i) => ({ ...b, order: i }));
    });
    setHasChanges(true);
  };

  const handleMoveDown = (index) => {
    setButtons(prev => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((b, i) => ({ ...b, order: i }));
    });
    setHasChanges(true);
  };

  const handleDeleteButton = (index) => {
    setButtons(prev => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i })));
    setHasChanges(true);
  };

  const handleAddButton = () => {
    if (!newButton.label.trim()) return;
    const btn = {
      id: `custom-${Date.now()}`,
      ...newButton,
      enabled: true,
      order: buttons.length,
    };
    setButtons(prev => [...prev, btn]);
    setNewButton({ icon: '📱', label: '', color: '#4299E1', actionType: 'navigation', actionTarget: '' });
    setAddDialogOpen(false);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await homeButtonsApi.saveForPerson(person.id, buttons);
      setSuccess('Home screen buttons saved successfully');
      setHasChanges(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save button configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setButtons(DEFAULT_BUTTONS.map((b, i) => ({ ...b, order: i })));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const enabledCount = buttons.filter(b => b.enabled).length;

  return (
    <Box>
      {/* Header info */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Configure which buttons {person.friendlyName} sees on the home screen
          </Typography>
          <Chip
            label={`${enabledCount} of ${buttons.length} visible`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<ResetIcon />}
            onClick={handleReset}
            color="inherit"
          >
            Reset
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Button list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {buttons.map((btn, index) => (
          <Paper
            key={btn.id}
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              opacity: btn.enabled ? 1 : 0.5,
              transition: 'opacity 0.2s',
            }}
          >
            {/* Reorder */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton
                size="small"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                sx={{ p: 0.25 }}
              >
                <UpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleMoveDown(index)}
                disabled={index === buttons.length - 1}
                sx={{ p: 0.25 }}
              >
                <DownIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Icon */}
            <Typography sx={{ fontSize: '24px', lineHeight: 1, width: 32, textAlign: 'center' }}>
              {btn.icon}
            </Typography>

            {/* Label & action */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {btn.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {btn.actionType}: {btn.actionTarget}
              </Typography>
            </Box>

            {/* Color indicator */}
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: btn.color,
                flexShrink: 0,
              }}
            />

            {/* Toggle */}
            <Switch
              checked={btn.enabled}
              onChange={() => handleToggle(index)}
              size="small"
            />

            {/* Delete (only custom buttons) */}
            {String(btn.id).startsWith('custom-') && (
              <IconButton size="small" onClick={() => handleDeleteButton(index)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        ))}
      </Box>

      {/* Save button */}
      <Button
        variant="contained"
        fullWidth
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving || !hasChanges}
      >
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>

      {/* Preview */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Preview
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: 1,
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
        }}
      >
        {buttons.filter(b => b.enabled).map((btn) => (
          <Box
            key={btn.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              p: 1,
              backgroundColor: 'white',
              borderRadius: 2,
              border: `1.5px solid ${btn.color}20`,
            }}
          >
            <Typography sx={{ fontSize: '20px', lineHeight: 1 }}>{btn.icon}</Typography>
            <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
              {btn.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Add Custom Button Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Button</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Icon (emoji)"
                value={newButton.icon}
                onChange={(e) => setNewButton(prev => ({ ...prev, icon: e.target.value }))}
                size="small"
                sx={{ width: 100 }}
              />
              <TextField
                label="Label"
                value={newButton.label}
                onChange={(e) => setNewButton(prev => ({ ...prev, label: e.target.value }))}
                size="small"
                fullWidth
                required
              />
            </Box>
            <TextField
              label="Color"
              type="color"
              value={newButton.color}
              onChange={(e) => setNewButton(prev => ({ ...prev, color: e.target.value }))}
              size="small"
              fullWidth
            />
            <Select
              value={newButton.actionType}
              onChange={(e) => setNewButton(prev => ({ ...prev, actionType: e.target.value }))}
              size="small"
              fullWidth
            >
              {ACTION_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <TextField
              label="Action Target"
              value={newButton.actionTarget}
              onChange={(e) => setNewButton(prev => ({ ...prev, actionTarget: e.target.value }))}
              size="small"
              fullWidth
              placeholder={newButton.actionType === 'navigate' ? '/page-path' : newButton.actionType === 'call' ? '1001' : 'https://...'}
              helperText={
                newButton.actionType === 'navigate' ? 'In-app path (e.g. /contacts)' :
                newButton.actionType === 'call' ? 'SIP extension to call' :
                'Target URL or action'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddButton}
            disabled={!newButton.label.trim()}
          >
            Add Button
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeScreenButtonsTab;
